import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  useColorScheme,
  Switch,
  FlatList,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { PieChart } from 'react-native-chart-kit';
import { Dimensions } from 'react-native';

// 重用之前页面中模拟的食物数据
const mockFoodData = [
  {
    id: '1',
    name: '米饭',
    pinyin: 'mifan',
    category: '谷物类',
    nutrients: { calories: 116, protein: 2.6, carbs: 25.6, fat: 0.3 },
    imageUrl: 'https://via.placeholder.com/60',
    portion: { default: 100, unit: '克' },
  },
  {
    id: '3',
    name: '鸡胸肉',
    pinyin: 'jixiongrou',
    category: '肉类',
    nutrients: { calories: 165, protein: 31.0, carbs: 0, fat: 3.6 },
    imageUrl: 'https://via.placeholder.com/60',
    portion: { default: 100, unit: '克' },
  },
  {
    id: '5',
    name: '西兰花',
    pinyin: 'xilanhua',
    category: '蔬菜类',
    nutrients: { calories: 34, protein: 2.8, carbs: 6.6, fat: 0.4 },
    imageUrl: 'https://via.placeholder.com/60',
    portion: { default: 100, unit: '克' },
  },
  {
    id: '6',
    name: '苹果',
    pinyin: 'pingguo',
    category: '水果类',
    nutrients: { calories: 52, protein: 0.3, carbs: 13.8, fat: 0.2 },
    imageUrl: 'https://via.placeholder.com/60',
    portion: { default: 100, unit: '克' },
  },
  {
    id: '7',
    name: '牛奶',
    pinyin: 'niunai',
    category: '乳制品',
    nutrients: { calories: 42, protein: 3.4, carbs: 5.0, fat: 1.0 },
    imageUrl: 'https://via.placeholder.com/60',
    portion: { default: 100, unit: '毫升' },
  },
];

const screenWidth = Dimensions.get('window').width;

const PersonalNutritionScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [isEditing, setIsEditing] = useState(false);
  
  // 营养目标状态
  const [nutritionGoals, setNutritionGoals] = useState({
    calories: 2000,
    protein: 100,
    carbs: 250,
    fat: 70,
  });
  
  // 临时状态，用于编辑
  const [tempGoals, setTempGoals] = useState({...nutritionGoals});
  
  // 推荐食物
  const [recommendedFoods, setRecommendedFoods] = useState([]);
  
  useEffect(() => {
    // 加载个人营养目标（实际应用中应从本地存储或数据库获取）
    
    // 生成推荐食物
    generateRecommendations();
  }, [nutritionGoals]);
  
  // 根据营养目标生成食物推荐
  const generateRecommendations = () => {
    // 在实际应用中，这里应该有更复杂的推荐算法
    // 这里只是简单地根据蛋白质和碳水比例推荐食物
    
    const proteinPercentage = (nutritionGoals.protein * 4 / nutritionGoals.calories) * 100;
    const carbsPercentage = (nutritionGoals.carbs * 4 / nutritionGoals.calories) * 100;
    
    let recommendations = [];
    
    // 根据营养目标选择适合的食物
    if (proteinPercentage > 25) {
      // 高蛋白饮食，推荐富含蛋白质的食物
      const highProteinFoods = mockFoodData.filter(
        food => (food.nutrients.protein * 4) / food.nutrients.calories > 0.3
      );
      recommendations = [...recommendations, ...highProteinFoods];
    }
    
    if (carbsPercentage < 40) {
      // 低碳饮食，推荐低碳水食物
      const lowCarbFoods = mockFoodData.filter(
        food => (food.nutrients.carbs * 4) / food.nutrients.calories < 0.3
      );
      recommendations = [...recommendations, ...lowCarbFoods];
    } else {
      // 正常或高碳饮食，推荐优质碳水来源
      const goodCarbFoods = mockFoodData.filter(
        food => food.category === '谷物类' || food.category === '水果类'
      );
      recommendations = [...recommendations, ...goodCarbFoods];
    }
    
    // 确保推荐是唯一的，并限制数量
    const uniqueRecommendations = Array.from(
      new Map(recommendations.map(item => [item.id, item])).values()
    ).slice(0, 5);
    
    setRecommendedFoods(uniqueRecommendations);
  };
  
  // 开始编辑
  const startEditing = () => {
    setTempGoals({...nutritionGoals});
    setIsEditing(true);
  };
  
  // 保存编辑
  const saveChanges = () => {
    // 验证输入
    const calories = parseFloat(tempGoals.calories);
    const protein = parseFloat(tempGoals.protein);
    const carbs = parseFloat(tempGoals.carbs);
    const fat = parseFloat(tempGoals.fat);
    
    if (isNaN(calories) || calories <= 0) {
      Alert.alert('提示', '请输入有效的卡路里值');
      return;
    }
    
    if (isNaN(protein) || protein <= 0) {
      Alert.alert('提示', '请输入有效的蛋白质值');
      return;
    }
    
    if (isNaN(carbs) || carbs <= 0) {
      Alert.alert('提示', '请输入有效的碳水化合物值');
      return;
    }
    
    if (isNaN(fat) || fat <= 0) {
      Alert.alert('提示', '请输入有效的脂肪值');
      return;
    }
    
    // 营养的基本合理性检查
    const totalCalories = protein * 4 + carbs * 4 + fat * 9;
    const calorieDeviation = Math.abs(totalCalories - calories) / calories;
    
    if (calorieDeviation > 0.1) { // 允许10%的误差
      Alert.alert(
        '营养目标不平衡',
        `您设置的宏量营养素总热量(${totalCalories.toFixed(0)}千卡)与目标热量(${calories}千卡)相差较大，请调整。`,
        [
          { text: '我知道了', style: 'cancel' },
          { 
            text: '自动调整', 
            onPress: () => {
              // 按比例调整宏量营养素
              const ratio = calories / totalCalories;
              setTempGoals({
                calories: calories,
                protein: Math.round(protein * ratio),
                carbs: Math.round(carbs * ratio),
                fat: Math.round(fat * ratio),
              });
            } 
          }
        ]
      );
      return;
    }
    
    // 保存修改
    setNutritionGoals({...tempGoals});
    setIsEditing(false);
    
    // 在实际应用中，这里应该更新本地存储或数据库
  };
  
  // 取消编辑
  const cancelEditing = () => {
    setIsEditing(false);
  };
  
  // 计算宏量营养素比例
  const calculateMacroPercentages = () => {
    const proteinCalories = nutritionGoals.protein * 4;
    const carbsCalories = nutritionGoals.carbs * 4;
    const fatCalories = nutritionGoals.fat * 9;
    const totalCalories = proteinCalories + carbsCalories + fatCalories;
    
    return {
      protein: Math.round((proteinCalories / totalCalories) * 100),
      carbs: Math.round((carbsCalories / totalCalories) * 100),
      fat: Math.round((fatCalories / totalCalories) * 100),
    };
  };
  
  const macroPercentages = calculateMacroPercentages();
  
  // 营养饼图数据
  const chartData = [
    {
      name: "蛋白质",
      population: nutritionGoals.protein * 4,
      color: "#3b82f6",
      legendFontColor: isDarkMode ? "#e0e0e0" : "#7e7e7e",
      legendFontSize: 13
    },
    {
      name: "碳水",
      population: nutritionGoals.carbs * 4,
      color: "#10b981",
      legendFontColor: isDarkMode ? "#e0e0e0" : "#7e7e7e",
      legendFontSize: 13
    },
    {
      name: "脂肪",
      population: nutritionGoals.fat * 9,
      color: "#f97316",
      legendFontColor: isDarkMode ? "#e0e0e0" : "#7e7e7e",
      legendFontSize: 13
    }
  ];
  
  // 渲染推荐食物列表项
  const renderFoodItem = ({ item }) => (
    <TouchableOpacity
      style={{
        flexDirection: 'row',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: isDarkMode ? '#7284a8' : '#e5e7eb',
        backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
        borderRadius: 8,
        marginBottom: 8,
      }}
      onPress={() => navigation.navigate('FoodDetail', { foodId: item.id })}
    >
      <Image
        source={{ uri: item.imageUrl }}
        style={{
          width: 60,
          height: 60,
          borderRadius: 8,
          marginRight: 16,
        }}
      />
      <View style={{ flex: 1 }}>
        <Text style={{
          fontSize: 16,
          fontWeight: 'bold',
          marginBottom: 4,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          {item.name}
        </Text>
        <Text style={{
          fontSize: 14,
          color: isDarkMode ? '#e0e0e0' : '#6b7280',
          marginBottom: 4,
        }}>
          {item.category}
        </Text>
        <Text style={{
          fontSize: 14,
          color: isDarkMode ? '#e0e0e0' : '#6b7280',
        }}>
          {item.nutrients.calories} 千卡 / {item.portion.default}{item.portion.unit}
        </Text>
      </View>
      <Ionicons name="chevron-forward" size={24} color={isDarkMode ? '#e0e0e0' : '#9ca3af'} />
    </TouchableOpacity>
  );
  
  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#ffffff',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      {/* 标题和编辑按钮 */}
      <View style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
      }}>
        <Text style={{
          fontSize: 22,
          fontWeight: 'bold',
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          个人营养目标
        </Text>
        
        {isEditing ? (
          <View style={{ flexDirection: 'row' }}>
            <TouchableOpacity
              onPress={cancelEditing}
              style={{
                paddingVertical: 8,
                paddingHorizontal: 16,
                backgroundColor: isDarkMode ? '#7284a8' : '#f3f4f6',
                borderRadius: 8,
                marginRight: 8,
              }}
            >
              <Text style={{
                color: isDarkMode ? 'white' : '#4b5563',
                fontWeight: '600',
              }}>
                取消
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={saveChanges}
              style={{
                paddingVertical: 8,
                paddingHorizontal: 16,
                backgroundColor: '#2563eb',
                borderRadius: 8,
              }}
            >
              <Text style={{
                color: 'white',
                fontWeight: '600',
              }}>
                保存
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            onPress={startEditing}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: 8,
              paddingHorizontal: 16,
              backgroundColor: '#2563eb',
              borderRadius: 8,
            }}
          >
            <Ionicons name="create-outline" size={20} color="white" style={{ marginRight: 4 }} />
            <Text style={{
              color: 'white',
              fontWeight: '600',
            }}>
              编辑
            </Text>
          </TouchableOpacity>
        )}
      </View>
      
      {/* 营养目标摘要 */}
      <View style={{
        backgroundColor: isDarkMode ? '#a9b6d0' : '#f0f9ff',
        padding: 16,
        borderRadius: 12,
        marginBottom: 24,
      }}>
        <Text style={{
          fontSize: 18,
          fontWeight: 'bold',
          marginBottom: 16,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          每日营养目标概览
        </Text>
        
        {/* 卡路里目标 */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          <Text style={{
            fontSize: 16,
            color: isDarkMode ? 'white' : '#4b5563',
          }}>
            目标热量
          </Text>
          
          {isEditing ? (
            <TextInput
              style={{
                borderWidth: 1,
                borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                borderRadius: 8,
                padding: 8,
                width: 100,
                textAlign: 'center',
                color: isDarkMode ? 'white' : '#1f2937',
                backgroundColor: isDarkMode ? '#7284a8' : 'white',
              }}
              placeholder="2000"
              placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
              value={tempGoals.calories.toString()}
              onChangeText={(text) => setTempGoals({...tempGoals, calories: text})}
              keyboardType="numeric"
            />
          ) : (
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              {nutritionGoals.calories} 千卡
            </Text>
          )}
        </View>
        
        {/* 营养素比例饼图 */}
        <View style={{ alignItems: 'center', marginBottom: 16 }}>
          <PieChart
            data={chartData}
            width={screenWidth - 64}
            height={180}
            chartConfig={{
              backgroundGradientFrom: isDarkMode ? '#a9b6d0' : '#ffffff',
              backgroundGradientTo: isDarkMode ? '#a9b6d0' : '#ffffff',
              color: (opacity = 1) => isDarkMode ? `rgba(255, 255, 255, ${opacity})` : `rgba(0, 0, 0, ${opacity})`,
            }}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="0"
            absolute
          />
        </View>
        
        {/* 宏量营养素目标 */}
        <View>
          {/* 蛋白质 */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: '#3b82f6',
                marginRight: 8,
              }} />
              <Text style={{
                fontSize: 16,
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                蛋白质
              </Text>
            </View>
            
            {isEditing ? (
              <TextInput
                style={{
                  borderWidth: 1,
                  borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                  borderRadius: 8,
                  padding: 8,
                  width: 80,
                  textAlign: 'center',
                  color: isDarkMode ? 'white' : '#1f2937',
                  backgroundColor: isDarkMode ? '#7284a8' : 'white',
                }}
                placeholder="100"
                placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
                value={tempGoals.protein.toString()}
                onChangeText={(text) => setTempGoals({...tempGoals, protein: text})}
                keyboardType="numeric"
              />
            ) : (
              <Text style={{
                fontSize: 16,
                fontWeight: '500',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {nutritionGoals.protein}克 ({macroPercentages.protein}%)
              </Text>
            )}
          </View>
          
          {/* 碳水化合物 */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: '#10b981',
                marginRight: 8,
              }} />
              <Text style={{
                fontSize: 16,
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                碳水化合物
              </Text>
            </View>
            
            {isEditing ? (
              <TextInput
                style={{
                  borderWidth: 1,
                  borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                  borderRadius: 8,
                  padding: 8,
                  width: 80,
                  textAlign: 'center',
                  color: isDarkMode ? 'white' : '#1f2937',
                  backgroundColor: isDarkMode ? '#7284a8' : 'white',
                }}
                placeholder="250"
                placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
                value={tempGoals.carbs.toString()}
                onChangeText={(text) => setTempGoals({...tempGoals, carbs: text})}
                keyboardType="numeric"
              />
            ) : (
              <Text style={{
                fontSize: 16,
                fontWeight: '500',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {nutritionGoals.carbs}克 ({macroPercentages.carbs}%)
              </Text>
            )}
          </View>
          
          {/* 脂肪 */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: '#f97316',
                marginRight: 8,
              }} />
              <Text style={{
                fontSize: 16,
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                脂肪
              </Text>
            </View>
            
            {isEditing ? (
              <TextInput
                style={{
                  borderWidth: 1,
                  borderColor: isDarkMode ? '#7284a8' : '#d1d5db',
                  borderRadius: 8,
                  padding: 8,
                  width: 80,
                  textAlign: 'center',
                  color: isDarkMode ? 'white' : '#1f2937',
                  backgroundColor: isDarkMode ? '#7284a8' : 'white',
                }}
                placeholder="70"
                placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
                value={tempGoals.fat.toString()}
                onChangeText={(text) => setTempGoals({...tempGoals, fat: text})}
                keyboardType="numeric"
              />
            ) : (
              <Text style={{
                fontSize: 16,
                fontWeight: '500',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {nutritionGoals.fat}克 ({macroPercentages.fat}%)
              </Text>
            )}
          </View>
        </View>
      </View>
      
      {/* 营养建议 */}
      <View style={{
        backgroundColor: isDarkMode ? '#a9b6d0' : '#f0f9ff',
        padding: 16,
        borderRadius: 12,
        marginBottom: 24,
      }}>
        <Text style={{
          fontSize: 18,
          fontWeight: 'bold',
          marginBottom: 16,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          营养建议
        </Text>
        
        <View style={{
          backgroundColor: isDarkMode ? '#7284a8' : '#e0f2fe',
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
        }}>
          <Text style={{
            fontSize: 16,
            color: isDarkMode ? 'white' : '#0c4a6e',
            lineHeight: 22,
          }}>
            {macroPercentages.protein < 15 ? 
              "您的蛋白质摄入比例较低，可能不利于肌肉维护，建议适当增加优质蛋白摄入。" :
            macroPercentages.protein > 35 ?
              "您的蛋白质摄入比例较高，请确保摄入足够的水分，并关注肾脏健康。" :
            macroPercentages.carbs < 30 ?
              "您的碳水化合物摄入比例偏低，属于低碳饮食，适合减脂期，但可能影响高强度运动表现。" :
            macroPercentages.carbs > 60 ?
              "您的碳水化合物摄入比例较高，适合高强度运动人群，但注意选择优质碳水来源。" :
            macroPercentages.fat < 20 ?
              "您的脂肪摄入比例偏低，可能影响脂溶性维生素吸收，建议适当增加健康脂肪摄入。" :
            macroPercentages.fat > 40 ?
              "您的脂肪摄入比例较高，请注意心血管健康，选择不饱和脂肪酸为主的食物。" :
              "您的营养目标比例平衡，符合健康饮食原则，继续保持！"}
          </Text>
        </View>
        
        <Text style={{
          fontSize: 16,
          marginBottom: 8,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          为您推荐的食物:
        </Text>
        
        <FlatList
          data={recommendedFoods}
          keyExtractor={(item) => item.id}
          renderItem={renderFoodItem}
          scrollEnabled={false} // 防止嵌套滚动冲突
          ListEmptyComponent={
            <Text style={{
              fontSize: 16,
              color: isDarkMode ? '#e0e0e0' : '#6b7280',
              textAlign: 'center',
              marginTop: 12,
            }}>
              暂无推荐食物
            </Text>
          }
        />
      </View>
      
      {/* 快速导航 */}
      <View style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}>
        <TouchableOpacity
          style={{
            flex: 1,
            padding: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : '#f0f9ff',
            borderRadius: 12,
            alignItems: 'center',
            marginRight: 8,
          }}
          onPress={() => navigation.navigate('FoodSearch')}
        >
          <Ionicons name="search" size={24} color="#2563eb" style={{ marginBottom: 8 }} />
          <Text style={{
            fontSize: 16,
            fontWeight: '500',
            color: isDarkMode ? 'white' : '#1f2937',
          }}>
            搜索食物
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={{
            flex: 1,
            padding: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : '#f0f9ff',
            borderRadius: 12,
            alignItems: 'center',
            marginLeft: 8,
          }}
          onPress={() => navigation.navigate('FoodFavorites')}
        >
          <Ionicons name="heart" size={24} color="#ec4899" style={{ marginBottom: 8 }} />
          <Text style={{
            fontSize: 16,
            fontWeight: '500',
            color: isDarkMode ? 'white' : '#1f2937',
          }}>
            我的收藏
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

export default PersonalNutritionScreen; 